/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { Row, Col, Card, CardBody, Table, Button, Badge, Input } from "reactstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';

const CustomerReport = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState(null);

    // Filters
    const [status, setStatus] = useState(null);
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" }
    ];

    const loadReport = async () => {
        dispatch(loadingTrue());
        let url = `/reports/customers.php?1=1`; // Machan un file path check panniko
        if (status) url += `&status=${status.value}`;
        if (search) url += `&search=${search}`;
        if (fromDate) url += `&from_date=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&to_date=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setReportData(res.data);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => {
        loadReport();
    }, []);

    if (!reportData) return null;

    const { summary, customer_trend_chart, status_chart, gst_chart, list } = reportData;

    // Formatting Data for Charts
    const formattedStatusData = status_chart?.map(item => ({
        name: item.status.toUpperCase(),
        value: parseInt(item.total)
    })) || [];

    const formattedGstData = gst_chart?.map(item => ({
        name: item.gst_registered == 1 ? "GST Registered" : "Non-GST",
        value: parseInt(item.total)
    })) || [];

    const COLORS = ['#6366f1', '#f43f5e', '#10b981', '#f59e0b'];

    return (
        <div className="report-shell p-4 min-vh-100" style={{ backgroundColor: '#f8fafc' }}>

            {/* Header Area */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
                <div>
                    <h2 className="fw-bold text-slate-900 mb-0 tracking-tight">Customer Intelligence</h2>
                    <p className="text-slate-500 font-medium">Demographics and onboarding growth analytics</p>
                </div>

                {/* Modern Filter Bar */}
                <div className="shadow-sm d-flex align-items-center gap-2 p-2 bg-white rounded-pill border">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Input
                            type="text"
                            placeholder="Search company..."
                            className="border-0 shadow-none bg-transparent ms-3"
                            style={{ width: 150 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="vr mx-2" style={{ height: 20 }}></div>
                        <div style={{ width: 150 }}>
                            <Select
                                options={statusOptions}
                                value={status}
                                onChange={setStatus}
                                placeholder="Status"
                                isClearable
                                styles={{ control: (b) => ({ ...b, border: 0, boxShadow: 'none' }) }}
                            />
                        </div>
                        <div className="vr mx-2" style={{ height: 20 }}></div>
                        <DatePicker value={fromDate} onChange={setFromDate} slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 100 } } }} />
                        <span className="text-slate-300">/</span>
                        <DatePicker value={toDate} onChange={setToDate} slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 100 } } }} />
                        <Button color="indigo" className="rounded-pill px-4 fw-bold text-white shadow-sm ms-2" style={{ backgroundColor: '#6366f1', border: 0 }} onClick={loadReport}>
                            Filter
                        </Button>
                    </LocalizationProvider>
                </div>
            </div>

            {/* ⭐ Summary Stats */}
            <Row className="mb-5 g-4">
                {[
                    { label: "Total Customers", value: summary?.total_customers, color: "bg-indigo-600", icon: "bi-people-fill" },
                    { label: "Active Now", value: summary?.active_customers, color: "bg-emerald-500", icon: "bi-check-circle-fill" },
                    { label: "Inactive", value: summary?.inactive_customers, color: "bg-slate-400", icon: "bi-x-circle-fill" },
                    { label: "GST Registered", value: summary?.gst_customers, color: "bg-amber-500", icon: "bi-shield-check" }
                ].map((item, idx) => (
                    <Col key={idx} md={3}>
                        <div className={`p-4 rounded-4 ${item.color}  border-0 shadow-lg d-flex align-items-center justify-content-between`}>
                            <div>
                                <span className="opacity-75 small uppercase fw-bold ls-1">{item.label}</span>
                                <h2 className="fw-bold mb-0 mt-1">{item.value || 0}</h2>
                            </div>
                            <div className="bg-white bg-opacity-20 rounded-circle p-3"><i className={`bi ${item.icon} h4 mb-0`}></i></div>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* ⭐ Charts Section */}
            <Row className="mb-5 g-4">
                <Col lg={7}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <CardBody className="p-4">
                            <h6 className="fw-bold text-slate-800 mb-4">Onboarding Trend (New Customers)</h6>
                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <LineChart data={customer_trend_chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} tickFormatter={s => dayjs(s).format('DD MMM')} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} />
                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 0, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                        <Line type="stepAfter" dataKey="total" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: '#6366f1' }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={5}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <CardBody className="p-4">
                            <h6 className="fw-bold text-slate-800 mb-4 text-center">Status & Tax Segmentation</h6>
                            <div className="d-flex" style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={formattedStatusData} innerRadius={60} outerRadius={80} dataKey="value" paddingAngle={5}>
                                            {formattedStatusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} cornerRadius={5} />)}
                                        </Pie>
                                        <Pie data={formattedGstData} innerRadius={90} outerRadius={105} dataKey="value" paddingAngle={2}>
                                            {formattedGstData.map((e, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} cornerRadius={5} />)}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ⭐ Customer Directory Table */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="bg-white p-4 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-slate-900">Client Directory</h6>
                    <Button color="light" size="sm" className="border rounded-pill px-3 fw-bold small">Export to CSV</Button>
                </div>
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                        <thead className="bg-slate-50 text-slate-400 small uppercase">
                            <tr>
                                <th className="ps-4 py-3">Code</th>
                                <th>Company Name</th>
                                <th>UEN / Tax ID</th>
                                <th>Contact</th>
                                <th>GST Status</th>
                                <th className="text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="small">
                            {list?.map((row, idx) => (
                                <tr key={idx}>
                                    <td className="ps-4 text-indigo-600 fw-bold">{row.customer_code}</td>
                                    <td>
                                        <div className="fw-600 text-slate-700">{row.company_name}</div>
                                        <div className="text-slate-400 extra-small">{row.email}</div>
                                    </td>
                                    <td className="text-slate-500 font-monospace small">{row.uen}</td>
                                    <td>
                                        <div className="small font-medium">{row.contact_person}</div>
                                        <div className="extra-small text-muted">{row.phone}</div>
                                    </td>
                                    <td>
                                        {row.gst_registered == 1 ?
                                            <Badge color="light" className="text-dark border border-amber-100">GST Reg</Badge> :
                                            <Badge color="light" className="text-dark border border-slate-100">Exempt</Badge>
                                        }
                                    </td>
                                    <td className="text-center">
                                        <span className={`status-dot ${row.status}`}></span>
                                        <span className="text-capitalize">{row.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .report-shell { font-family: 'Plus Jakarta Sans', sans-serif; }
                .fw-600 { font-weight: 600; }
                .ls-1 { letter-spacing: 0.05em; }
                .font-monospace { font-family: 'Courier New', Courier, monospace; }
                .status-dot { height: 8px; width: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
                .status-dot.active { background-color: #10b981; box-shadow: 0 0 8px #10b981; }
                .status-dot.inactive { background-color: #94a3b8; }
                .table thead th { border-top: 0; font-size: 11px; }
                .table tbody tr:hover { background-color: #f8fafc !important; }
            `}</style>
        </div>
    );
};

export default CustomerReport;